import React, { useState, useEffect } from 'react';
import { message, DatePicker, Tabs, Switch, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  TransactionHeader,
  TransactionStatistics,
} from '../component/transactions';
import TransactionService from '../service/transaction-service/Transaction.service';
import { FinancialService } from '../service/financial-service/financial.service';
import useEmployee from '../hook/useEmployee';
import { useIsAdmin } from '../hook/useRole';
import ExportableTable from '../component/common/ExportableTable';
import {
  exportMaturityTransactionsToExcel,
  exportFinancialRecordsToExcel,
  exportCombinedDataToExcel,
} from '../utils/excelExport';
import { useCardDetail } from '../context/CardDetailContext';
import { Input, Button } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { matchesSearch } from '../utils/searchText';

dayjs.extend(utc);

const { TabPane } = Tabs;
const { Text } = Typography;

// Interface cho record kết hợp
interface CombinedRecord {
  id: number;
  date: string; // Ngày chung
  type: 'transaction' | 'financial'; // Loại record
  subType: string; // Loại con: 'withdrawal', 'deposit', 'INCOME', 'EXPENSE'
  amount: number;
  description: string; // Mô tả hoặc thông tin thẻ
  employee?: {
    id: number;
    name: string;
  };
  status?: string;
  // Thông tin giao dịch (nếu có)
  creditCard?: {
    id: number;
    cardNumber: string;
    cardHolderName?: string;
    bank?: {
      name: string;
    };
  };
  feeApplied?: number;
  posDevice?: {
    id?: number;
    deviceCode: string;
    location?: string;
  };
  // Thông tin thu chi (nếu có)
  recordType?: string;
  category?: string;
  createdBy?: string;
  createdAt?: string;
}

const MaturityTransactionsByMonth: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedCard, setMaturityScheduleId, setMaturityYear } =
    useCardDetail();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [financialRecords, setFinancialRecords] = useState<any[]>([]);
  const [combinedData, setCombinedData] = useState<CombinedRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [showCombinedView, setShowCombinedView] = useState(true);
  const [activeTab, setActiveTab] = useState('combined');

  const { employee } = useEmployee();
  const isAdmin = useIsAdmin();

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  // Format card number
  const formatCardNumber = (cardNumber?: string): string => {
    if (!cardNumber) return 'N/A';
    if (cardNumber.length < 4) return cardNumber;
    return `***${cardNumber.slice(-4)}`;
  };

  // Load transactions
  const loadTransactions = async (date: Dayjs) => {
    if (!employee?.id && !isAdmin) return;
    setLoading(true);
    try {
      const year = date.year();
      const monthNum = date.month() + 1;
      const day = date.date();
      let response: any;

      if (isAdmin) {
        response = await TransactionService.getMaturityTransactionsByDate(
          year,
          monthNum,
          day,
          0,
          10000
        );
      } else {
        response =
          await TransactionService.getMaturityTransactionsByMonthAndEmployee(
            year,
            monthNum,
            day,
            employee?.id || 0,
            0,
            10000
          );
      }
      setTransactions(response.content);
    } catch (error) {
      console.error('Error loading transactions:', error);
      message.error('Không thể tải giao dịch');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Load financial records that are related to transactions
  const loadFinancialRecords = async (date: Dayjs) => {
    try {
      const dateString = date.format('YYYY-MM-DD');
      let relatedRecords: any[] = [];
      if (isAdmin) {
        relatedRecords =
          await FinancialService.getRecordsWithRelatedTransactionByDate(
            dateString
          );
      } else if (employee?.id) {
        relatedRecords =
          await FinancialService.getRecordsWithRelatedTransactionByDateAndEmployee(
            dateString,
            employee.id
          );
      }
      console.log(
        `Loaded ${relatedRecords.length} financial records with relatedTransactionId for date ${date.format('DD/MM/YYYY')}`
      );
      setFinancialRecords(relatedRecords);
    } catch (error) {
      console.error('Error loading financial records:', error);
      message.error('Không thể tải bản ghi thu chi');
      setFinancialRecords([]);
    }
  };

  // Kết hợp dữ liệu
  const combineData = () => {
    const combined: CombinedRecord[] = [];

    // Thêm giao dịch
    transactions.forEach((transaction) => {
      combined.push({
        id: transaction.id,
        date: transaction.transactionDate,
        type: 'transaction',
        subType: transaction.transactionType,
        amount: transaction.amount,
        description: transaction.creditCard?.cardHolderName
          ? `${transaction.creditCard.cardHolderName} - ***${transaction.creditCard.cardNumber?.slice(-4)}`
          : `***${transaction.creditCard?.cardNumber?.slice(-4) || 'N/A'}`,
        employee: transaction.employee,
        status: transaction.status,
        creditCard: transaction.creditCard,
        feeApplied: transaction.feeApplied,
        posDevice: transaction.posDevice
          ? {
            id: transaction.posDevice.id,
            deviceCode: transaction.posDevice.deviceCode,
            location: transaction.posDevice.location,
          }
          : undefined,
        createdAt: transaction.createdAt,
      });
    });

    // Thêm thu chi - SỬA: Sử dụng recordDate (ngày thực hiện thu chi)
    financialRecords.forEach((record) => {
      combined.push({
        id: record.id,
        date: record.recordDate, // Sử dụng recordDate (ngày thực hiện thu chi)
        type: 'financial',
        subType: record.recordType,
        amount: record.amount,
        description: record.description,
        recordType: record.recordType,
        category: record.category,
        createdBy: record.createdBy,
        createdAt: record.createdAt,
      });
    });

    // SỬA: Sắp xếp theo recordDate (ngày thực hiện) thay vì createdAt
    combined.sort((a, b) => {
      // Sử dụng recordDate cho financial records, createdAt cho transactions
      const timeA =
        a.type === 'financial'
          ? dayjs(a.date).unix()
          : a.createdAt
            ? dayjs(a.createdAt).unix()
            : 0;
      const timeB =
        b.type === 'financial'
          ? dayjs(b.date).unix()
          : b.createdAt
            ? dayjs(b.createdAt).unix()
            : 0;

      // Sắp xếp giảm dần: mới nhất lên đầu
      return timeB - timeA;
    });

    setCombinedData(combined);
  };

  // Handle date change
  const handleDateChange = (date: Dayjs) => {
    setSelectedDate(date);
    loadTransactions(date);
    loadFinancialRecords(date);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadTransactions(selectedDate);
    loadFinancialRecords(selectedDate);
  };

  // Handle view detail navigation
  const handleViewDetail = (navigationData: {
    creditCardId: number;
    creditCardNumber: string;
    maturityScheduleId: number;
    maturityYear: number;
  }) => {
    // Find the transaction that contains the full credit card object
    const transaction = transactions.find(
      (t) => t.creditCard?.id === navigationData.creditCardId
    );

    if (transaction?.creditCard) {
      // Set data in context with full credit card object
      setSelectedCard(transaction.creditCard);
      setMaturityScheduleId(navigationData.maturityScheduleId);
      setMaturityYear(navigationData.maturityYear);

      // Navigate to card detail page without params
      navigate('/card-detail');
    }
  };

  // Load data on component mount and when date changes
  useEffect(() => {
    if (employee?.id || isAdmin) {
      loadTransactions(selectedDate);
      loadFinancialRecords(selectedDate);
    }
  }, [employee?.id, isAdmin, selectedDate]);

  // Combine data when both are loaded
  useEffect(() => {
    combineData();
  }, [transactions, financialRecords]);

  // Columns cho bảng kết hợp
  const combinedColumns = [
    {
      title: 'Thời gian',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : []);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            format="DD/MM/YYYY"
            showTime={false}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: CombinedRecord) => {
        const recordDate = dayjs(record.date).format('YYYY-MM-DD');
        return recordDate === value;
      },
      render: (date: string, record: CombinedRecord) => {
        if (record.type === 'transaction' && record.createdAt) {
          // Backend đã trả createdAt theo giờ VN (Asia/Ho_Chi_Minh), hiển thị trực tiếp
          return dayjs(record.createdAt).format('DD-MM-YYYY HH:mm');
        } else {
          // Với thu chi: Sử dụng createdAt để hiển thị thời gian chính xác
          if (record.createdAt) {
            return dayjs(record.createdAt).format('DD-MM-YYYY HH:mm');
          } else {
            return dayjs(date).format('DD-MM-YYYY HH:mm');
          }
        }
      },
    },
    {
      title: 'Loại',
      key: 'category',
      width: 120,
      filters: [
        { text: 'Rút tiền', value: 'withdrawal' },
        { text: 'Nạp tiền', value: 'deposit' },
        { text: 'Thu', value: 'INCOME' },
        { text: 'Chi', value: 'EXPENSE' },
      ],
      onFilter: (value: string, record: CombinedRecord) =>
        record.subType === value,
      render: (value: any, record: CombinedRecord) => {
        if (record.type === 'transaction') {
          return (
            <span
              className={`text-sm font-medium ${record.subType === 'withdrawal'
                  ? 'text-green-500'
                  : 'text-blue-600'
                }`}
            >
              {record.subType === 'withdrawal' ? 'Rút tiền' : 'Nạp tiền'}
            </span>
          );
        } else {
          return (
            <span
              className={`text-sm font-medium ${record.subType === 'INCOME' ? 'text-green-600' : 'text-red-600'
                }`}
            >
              {record.subType === 'INCOME' ? 'Thu' : 'Chi'}
            </span>
          );
        }
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: CombinedRecord) =>
        record.amount.toString().includes(value),
      render: (amount: number, record: CombinedRecord) => (
        <span
          className={`font-semibold ${record.type === 'financial'
              ? record.subType === 'INCOME'
                ? 'text-green-600'
                : 'text-red-600'
              : 'text-blue-600'
            }`}
        >
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Mô tả/Thông tin',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm mô tả"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: CombinedRecord) =>
        matchesSearch(record.description, value),
      render: (description: string, record: CombinedRecord) => (
        <div>
          <div className="font-medium">{description}</div>
          {record.type === 'transaction' && record.creditCard?.bank?.name && (
            <div className="text-xs text-gray-500">
              {record.creditCard.bank.name}
            </div>
          )}
          {record.type === 'financial' && record.category && (
            <div className="text-xs text-gray-500">{record.category}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Người thực hiện',
      key: 'performer',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm người thực hiện"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: CombinedRecord) => {
        const performer =
          record.type === 'transaction'
            ? record.employee?.name || ''
            : record.createdBy || '';
        return matchesSearch(performer, value);
      },
      render: (value: any, record: CombinedRecord) => (
        <span className="font-medium">
          {record.type === 'transaction'
            ? record.employee?.name || 'N/A'
            : record.createdBy || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'statusOrExtra',
      width: 120,
      filters: [
        { text: 'Hoàn thành', value: 'COMPLETED' },
        { text: 'Đang chờ', value: 'PENDING' },
        { text: 'Giao dịch', value: 'transaction' },
        { text: 'Thu chi', value: 'financial' },
      ],
      onFilter: (value: string, record: CombinedRecord) => {
        if (value === 'transaction' || value === 'financial') {
          return record.type === value;
        }
        return record.status === value;
      },
      render: (value: any, record: CombinedRecord) => {
        if (record.type === 'transaction') {
          return (
            <div>
              <div
                className={`px-2 py-1 rounded-full text-xs font-semibold ${record.status === 'COMPLETED'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                  }`}
              >
                {record.status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ'}
              </div>
            </div>
          );
        } else {
          return (
            <span className="text-xs text-gray-500">
              {dayjs(record.createdAt).format('HH:mm')}
            </span>
          );
        }
      },
    },
    {
      title: 'Thiết bị POS',
      key: 'posDevice',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => {
        // Lấy danh sách tất cả POS devices từ transactions
        const allPosDevices = transactions
          .map((t) => t.posDevice?.deviceCode)
          .filter(Boolean)
          .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
          .sort();

        return (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Chọn thiết bị POS:</Text>
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {allPosDevices.map((deviceCode) => (
                <div key={deviceCode} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(deviceCode)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKeys([...selectedKeys, deviceCode]);
                        } else {
                          setSelectedKeys(
                            selectedKeys.filter((key) => key !== deviceCode)
                          );
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <span>{deviceCode}</span>
                  </label>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Lọc
              </Button>
              <Button
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </div>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: CombinedRecord) => {
        const posDeviceCode = record.posDevice?.deviceCode || '';
        return posDeviceCode === value;
      },
      render: (value: any, record: CombinedRecord) => {
        if (record.type === 'transaction' && record.posDevice?.deviceCode) {
          return (
            <span className="text-sm font-medium text-blue-600">
              {record.posDevice.deviceCode}
            </span>
          );
        } else {
          return <span className="text-gray-400">-</span>;
        }
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (value: any, record: CombinedRecord) => {
        if (record.type === 'transaction' && record.creditCard) {
          return (
            <button
              onClick={() =>
                handleViewDetail({
                  creditCardId: record.creditCard!.id,
                  creditCardNumber: record.creditCard!.cardNumber,
                  maturityScheduleId: 0,
                  maturityYear: dayjs(record.date).year(),
                })
              }
              className="text-purple-600 hover:underline hover:text-purple-800 font-medium"
            >
              Chi tiết
            </button>
          );
        }
        return null;
      },
    },
  ];

  // Columns cho giao dịch (giữ nguyên)
  const transactionColumns = [
    {
      title: 'Ngày giao dịch',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : []);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            format="DD/MM/YYYY"
            disabledDate={(current) => {
              // Chỉ cho phép chọn ngày trong tháng đã chọn
              return (
                !current ||
                current.month() !== selectedDate.month() ||
                current.year() !== selectedDate.year()
              );
            }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) => {
        const recordDate = dayjs(record.transactionDate).format('YYYY-MM-DD');
        return recordDate === value;
      },
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
    },

    {
      title: 'Loại giao dịch',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 120,
      filters: [
        { text: 'Rút tiền', value: 'withdrawal' },
        { text: 'Nạp tiền', value: 'deposit' },
      ],
      onFilter: (value: string, record: any) =>
        record.transactionType === value,
      render: (type: string) => (
        <span
          className={`text-sm font-medium ${type === 'withdrawal' ? 'text-green-500' : 'text-blue-600'}`}
        >
          {type === 'withdrawal' ? 'Rút tiền' : 'Nạp tiền'}
        </span>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        record.amount.toString().includes(value),
      render: (amount: number) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Số thẻ',
      dataIndex: ['creditCard', 'cardNumber'],
      key: 'cardNumber',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số thẻ"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        (record.creditCard?.cardNumber || '')
          .toLowerCase()
          .includes(value.toLowerCase()),
      render: (cardNumber: string) =>
        cardNumber ? (
          <span className="text-gray-700 font-medium">{`***${cardNumber.slice(-4)}`}</span>
        ) : (
          <span className="text-gray-400 italic">N/A</span>
        ),
    },
    {
      title: 'Chủ thẻ',
      dataIndex: ['creditCard', 'cardHolderName'],
      key: 'cardHolderName',
      width: 200,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm chủ thẻ"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        matchesSearch(record.creditCard?.cardHolderName, value),
      render: (value: string) =>
        value ? (
          <span className="text-gray-800">{value}</span>
        ) : (
          <span className="text-gray-400 italic">Không rõ</span>
        ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: [
        { text: 'Hoàn thành', value: 'COMPLETED' },
        { text: 'Đang chờ', value: 'PENDING' },
      ],
      onFilter: (value: string, record: any) => record.status === value,
      render: (status: string) => {
        const isCompleted = status === 'COMPLETED';
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${isCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
          >
            {isCompleted ? 'Hoàn thành' : 'Đang chờ'}
          </span>
        );
      },
    },
    {
      title: 'NV thực hiện',
      dataIndex: ['employee', 'name'],
      key: 'employeeName',
      width: 150,
      render: (value: string, record: any) =>
        value || record.employee?.name ? (
          <span className="text-gray-800 font-medium">
            {value || record.employee?.name}
          </span>
        ) : (
          <span className="text-gray-400 italic">Không rõ</span>
        ),
    },
    {
      title: 'Thiết bị POS',
      key: 'posDevice',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => {
        // Lấy danh sách tất cả POS devices từ transactions
        const allPosDevices = transactions
          .map((t) => t.posDevice?.deviceCode)
          .filter(Boolean)
          .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
          .sort();

        return (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Chọn thiết bị POS:</Text>
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {allPosDevices.map((deviceCode) => (
                <div key={deviceCode} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(deviceCode)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKeys([...selectedKeys, deviceCode]);
                        } else {
                          setSelectedKeys(
                            selectedKeys.filter((key) => key !== deviceCode)
                          );
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <span>{deviceCode}</span>
                  </label>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Lọc
              </Button>
              <Button
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </div>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) => {
        const posDeviceCode = record.posDevice?.deviceCode || '';
        return posDeviceCode === value;
      },
      render: (value: any, record: any) => {
        if (record.posDevice?.deviceCode) {
          return (
            <span className="text-sm font-medium text-blue-600">
              {record.posDevice.deviceCode}
            </span>
          );
        } else {
          return <span className="text-gray-400">-</span>;
        }
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (value: any, record: any) => (
        <button
          onClick={() =>
            handleViewDetail({
              creditCardId: record.creditCard?.id || 0,
              creditCardNumber: record.creditCard?.cardNumber || '',
              maturityScheduleId: record.maturitySchedule?.id || 0,
              maturityYear: dayjs(record.maturitySchedule?.dueDate).year(),
            })
          }
          className="text-purple-600 hover:underline hover:text-purple-800 font-medium"
        >
          Chi tiết
        </button>
      ),
    },
  ];

  // Columns cho thu chi (đơn giản hóa)
  const financialColumns = [
    {
      title: 'Ngày',
      dataIndex: 'recordDate',
      key: 'recordDate',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : []);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            format="DD/MM/YYYY"
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) => {
        const recordDate = dayjs(record.recordDate).format('YYYY-MM-DD');
        return recordDate === value;
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Loại',
      dataIndex: 'recordType',
      key: 'recordType',
      width: 100,
      render: (type: string) => (
        <span
          className={`px-2 py-1 rounded text-sm ${type === 'INCOME'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
            }`}
        >
          {type === 'INCOME' ? 'Thu' : 'Chi'}
        </span>
      ),
      filters: [
        { text: 'Thu', value: 'INCOME' },
        { text: 'Chi', value: 'EXPENSE' },
      ],
      onFilter: (value: string, record: any) => record.recordType === value,
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        record.amount.toString().includes(value),
      render: (amount: number, record: any) => (
        <span
          className={`font-semibold ${record.recordType === 'INCOME' ? 'text-green-600' : 'text-red-600'
            }`}
        >
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm mô tả"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        matchesSearch(record.description, value),
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm người tạo"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        matchesSearch(record.createdBy, value),
    },
  ];

  // Handle export cho dữ liệu kết hợp
  const handleCombinedExport = async (selectedRecords: CombinedRecord[]) => {
    await exportCombinedDataToExcel(
      selectedRecords,
      selectedDate.date(),
      selectedDate.month() + 1,
      selectedDate.year()
    );
  };

  const handleExport = async (selectedRecords: any[]) => {
    if (activeTab === 'combined') {
      await handleCombinedExport(selectedRecords);
    } else if (activeTab === 'transactions') {
      await exportMaturityTransactionsToExcel(
        selectedRecords,
        selectedDate.month() + 1,
        selectedDate.year()
      );
    } else {
      await exportFinancialRecordsToExcel(
        selectedRecords,
        selectedDate.month() + 1,
        selectedDate.year()
      );
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <TransactionHeader
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <TransactionStatistics
        transactions={transactions}
        financialRecords={financialRecords}
        activeTab={activeTab}
        loading={loading}
        formatCurrency={formatCurrency}
      />

      {/* Tab Navigation */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} className="mt-6">
        <TabPane tab="Tổng hợp" key="combined">
          <div className="mb-4">
            <Text type="secondary">
              Hiển thị tất cả giao dịch và thu chi liên quan đến giao dịch trong
              ngày được chọn
            </Text>
            {!isAdmin && (
              <div className="mt-2">
                <Text type="warning" className="text-sm">
                  💡 Bạn chỉ xem được giao dịch do bạn thực hiện và thu chi liên
                  quan (Nhân viên: {employee?.name})
                </Text>
              </div>
            )}
          </div>
          <ExportableTable
            key={`combined-${selectedDate.format('YYYY-MM-DD')}-${combinedData.length}`} // Force reset sort khi data thay đổi
            columns={combinedColumns}
            dataSource={combinedData}
            loading={loading}
            onExport={handleExport}
            exportTitle="Xuất báo cáo tổng hợp"
            isAdmin={isAdmin}
            pagination={{ showSizeChanger: false }}
          />
        </TabPane>

        <TabPane tab={`Giao dịch (${transactions.length})`} key="transactions">
          {!isAdmin && (
            <div className="mb-4">
              <Text type="secondary" className="text-sm">
                Hiển thị giao dịch do bạn thực hiện (Nhân viên: {employee?.name}
                )
              </Text>
            </div>
          )}
          <ExportableTable
            key={`transactions-${selectedDate.format('YYYY-MM-DD')}-${transactions.length}`} // Force reset sort khi data thay đổi
            columns={transactionColumns}
            dataSource={[...transactions].sort((a, b) => {
              const timeA = a.createdAt ? dayjs(a.createdAt).unix() : 0;
              const timeB = b.createdAt ? dayjs(b.createdAt).unix() : 0;
              return timeB - timeA; // Mới nhất lên đầu
            })}
            loading={loading}
            onExport={handleExport}
            exportTitle="Xuất báo cáo giao dịch đáo hạn"
            isAdmin={isAdmin}
            pagination={{ showSizeChanger: false }}
          />
        </TabPane>

        <TabPane tab={`Thu chi (${financialRecords.length})`} key="financial">
          <div className="mb-4">
            {isAdmin ? (
              <Text type="secondary" className="text-sm">
                Hiển thị tất cả bản ghi thu chi liên quan đến giao dịch trong
                ngày được chọn
              </Text>
            ) : (
              <div>
                <Text type="secondary" className="text-sm">
                  Hiển thị bản ghi thu chi liên quan đến giao dịch do bạn thực
                  hiện
                </Text>
                <br />
                <Text type="warning" className="text-sm">
                  💡 Nhân viên: {employee?.name} - Chỉ xem được thu chi liên
                  quan đến giao dịch của bạn
                </Text>
              </div>
            )}
          </div>
          <ExportableTable
            key={`financial-${selectedDate.format('YYYY-MM-DD')}-${financialRecords.length}`} // Force reset sort khi data thay đổi
            columns={financialColumns}
            dataSource={[...financialRecords].sort((a, b) => {
              const timeA = a.createdAt ? dayjs(a.createdAt).unix() : 0;
              const timeB = b.createdAt ? dayjs(b.createdAt).unix() : 0;
              return timeB - timeA; // Mới nhất lên đầu
            })}
            loading={loading}
            onExport={handleExport}
            exportTitle="Xuất báo cáo thu chi"
            isAdmin={isAdmin}
            pagination={{ showSizeChanger: false }}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default MaturityTransactionsByMonth;
